import React, { useMemo } from 'react'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { NoData } from 'src/components/analytics/common/NoData'
import { Download } from '@phosphor-icons/react'
import { getAnalyticsWidgetPayload, extractDateFromFilters } from 'src/lib/utils/analyticsHelpers'
import { arrayToCsv, downloadCsvHelper } from 'src/lib/utils/helpers'
import { analyticsConstants } from 'src/lib/utils/constants'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { errorToast } from 'src/lib/utils/toasters'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'
import { useChartData } from '../../useChartData'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { getAnalyticsWidgetData } from 'src/lib/core/api-methods'
import { ChartSkeleton } from '../common/Skeleton'
import { useLocale } from 'src/lib/hooks/useLocal'

type SurveyData = {
  name: string
  value: number
  rawCount: number
  percentageChange: number
}

const ExitCheckoutSurvey = () => {
  const { t } = useLocale()
  const {
    getters: { refreshKey, dateFilter }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.exit_checkout_survey,
      dependencies: {
        endpoints: ['/exit-checkout-survey']
      }
    },
    refreshKey
  })
  const { setLoaderProperties } = useLoader()
  const { sendActivityLog } = useActivityLogs()

  // Extract widget data using the same pattern as PaymentMethodsConversionRates
  const widgetData = useMemo(() => {
    return data?.[0]?.data?.result?.widgetData?.data ?? []
  }, [data])

  const totalCount = widgetData.find((d: any) => d.dimension === 'acr_survey_total_count')?.data?.[0]?.total ?? 0

  const processedData: SurveyData[] = useMemo(() => {
    const surveyCountData = widgetData.find((d: any) => d.dimension === 'acr_survey_data_count')?.data ?? []
    const surveyPercentageData = widgetData.find((d: any) => d.dimension === 'acr_survey_data_percentage')?.data ?? []

    if (!surveyCountData.length) return []
    return surveyCountData
      .map((item: any, idx: number) => {
        const label = item?.x ?? '-'
        const count = item?.y ?? 0
        const percentage = surveyPercentageData?.[idx]?.y ?? 0
        return {
          name: label,
          value: formatNumberToTwoDecimals(percentage),
          rawCount: count,
          percentageChange: 0 // Placeholder for future comparison
        }
      })
      .sort((a: SurveyData, b: SurveyData) => b.value - a.value)
  }, [widgetData])

  const maxValue = Math.max(...processedData.map(d => d.value), 100)

  const handleDownloadClick = async () => {
    setLoaderProperties({ isLoaderVisible: true, message: 'Downloading all responses...' })
    try {
      const response: any = await getAnalyticsWidgetData(
        getAnalyticsWidgetPayload(
          analyticsConstants.FLO_ACR_SURVEY_DATA_ALL,
          extractDateFromFilters(dateFilter, 'startDate'),
          extractDateFromFilters(dateFilter, 'endDate')
        )
      )
      const csvData = response?.data?.widgetData?.[0]?.data
      downloadCsvHelper(arrayToCsv(csvData, ['Message', 'Count']), `acr_surveys.csv`)
      sendActivityLog({
        entityName: EntityName.OVERVIEW,
        actionName: 'exported data'
      })
    } catch (err) {
      console.error(err)
      errorToast('Failed to download data.')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>{t('error')}</div>
    } else if (processedData?.length > 0) {
      return (
        <>
          <div
            className={`space-y-4 mt-4 flex-1 ${processedData.length > 9 ? 'overflow-y-auto custom-scrollbar' : ''}`}
          >
            {processedData.map(item => {
              const barWidth = `${(item.value / maxValue) * 100}%`
              return (
                <div key={item.name} className='flex flex-col space-y-2 px-2'>
                  <div className='flex justify-between'>
                    <div className='w-full text-sm'>{item.name}</div>
                    <div className='flex items-center gap-1'>
                      <div className='text-sm text-gray-dark'>{item.rawCount}</div>
                      <span className='bg-coal-light p-[1px] m-0'></span>
                      <div className='text-sm text-carbon'>{item.value}%</div>
                      <TrendIndicator value={item.percentageChange} formatter={val => `${Math.abs(val)}%`} />
                    </div>
                  </div>
                  <div className='w-full'>
                    <div className='relative h-0.5 bg-gray-100 rounded-full'>
                      <div
                        className='absolute left-0 top-0 h-[2px] bg-[#EFD080] rounded-full'
                        style={{ width: barWidth }}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className='flex flex-row w-full justify-between mt-6'>
            <p className='text-sm font-normal text-gray-dark'>{`${totalCount} responses`}</p>
            <button
              className='flex flex-row text-coal-dark text-sm font-semibold gap-1.5'
              onClick={handleDownloadClick}
            >
              <Download className='w-4 h-4' />
              <span>{t('all_responses')}</span>
            </button>
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full mt-2 pb-4 flex flex-col'>
      {renderComponent()}
    </div>
  )
}

export default ExitCheckoutSurvey
